import {
  EliminatedPerson,
  PeopleCandidate,
  Relationship,
  candidateDisplayName,
} from "@inrange/building-manager-api-client";
import React, { useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { RelationshipCell, ScrollableRows } from "./contactCellChrome";
import { bandLabel } from "./peopleDiscoveryBands";

// The two people tables of a Contacts cell. Split out of the cell because they are
// the bulk of its render and none of it needs the cell's operation state: what they
// draw is decided entirely by the props below, and the one thing they cannot decide
// for themselves - whether a row can be ticked, which depends on credits, opt-outs
// and pooling - arrives as `renderSelectCell`. That callback is only ever invoked
// from these components' own render, never from their caller's.

export const RankedCandidatesTable: React.FC<{
  candidates: PeopleCandidate[];
  // Rows the AI ranking never saw (in HubSpot, absent from Apollo's results), so the
  // band and reasoning columns say so rather than inventing a verdict.
  unranked: Set<string>;
  // The whole list is pre-ranking output: everyone found, nobody scored yet.
  provisional: boolean;
  // THIS cell's run is in flight, so the rows below are not its answer yet.
  rankingInFlight: boolean;
  progressMessage: string | undefined;
  relationshipOf: (apolloId: string) => Relationship;
  renderSelectCell: (apolloId: string | undefined) => React.ReactNode;
  onDecideMatch: (
    apolloId: string,
    contactId: string,
    verdict: "confirmed" | "rejected"
  ) => void;
  decisionBusy: boolean;
}> = ({
  candidates: allCandidates,
  unranked,
  provisional,
  rankingInFlight,
  progressMessage,
  relationshipOf,
  renderSelectCell,
  onDecideMatch,
  decisionBusy,
}) => (
  // While a run is in flight the rows below are NOT its answer - they are
  // either this run's unranked provisional set or the previous run's
  // ranked one, and both read as the result unless something says
  // otherwise. So the whole table is greyed and covered rather than given
  // a "loading" row: a row you cannot read past is unmistakable, whereas a
  // spinner above a full table just looks like more is coming. The cover
  // also stops a row being ticked while it is about to be replaced, which
  // would silently discard the selection. It goes on its own when the
  // ranked blob lands and re-renders the rows in the model's order.
  <div style={{ position: "relative" }}>
    {rankingInFlight && (
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255, 255, 255, 0.72)",
          borderRadius: 4,
          textAlign: "center",
          padding: 8,
        }}
      >
        <div className="text-muted" style={{ fontSize: 13 }}>
          <Spinner animation="border" size="sm" className="me-2 align-middle" />
          {progressMessage ?? "Finding people..."}
          <div style={{ fontSize: 12 }}>
            {provisional
              ? "These are the people we found - ranking them now."
              : "Showing the previous result until this run finishes."}
          </div>
        </div>
      </div>
    )}
    {provisional && !rankingInFlight && (
      <div className="text-muted mb-1" style={{ fontSize: 12 }}>
        The ranking didn’t finish, so these people are unranked - re-run to rank
        them. Nobody has been ruled out.
      </div>
    )}
    <div style={rankingInFlight ? { opacity: 0.45 } : undefined}>
      <ScrollableRows rowCount={allCandidates.length}>
        <Table
          size="sm"
          className="mt-2 mb-1 align-middle"
          style={{ fontSize: 12 }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#fff",
            }}
          >
            <tr className="text-muted">
              <th style={{ width: 26 }} />
              <th>Name</th>
              <th>Position</th>
              <th style={{ width: 196 }}>Relationship</th>
              <th>Priority</th>
              <th>Reasoning</th>
            </tr>
          </thead>
          <tbody>
            {allCandidates.map((person) => {
              const relationship = relationshipOf(person.apolloId);
              const blocked = relationship.kind === "do_not_contact";
              return (
                <tr key={person.apolloId}>
                  {blocked ? (
                    <td>
                      <Form.Check
                        type="checkbox"
                        disabled
                        checked={false}
                        readOnly
                        title="Opted out in HubSpot - cannot be contacted"
                      />
                    </td>
                  ) : (
                    renderSelectCell(person.apolloId)
                  )}
                  <td
                    className="fw-semibold text-nowrap"
                    style={blocked ? { color: "#6c757d" } : undefined}
                  >
                    {candidateDisplayName(person, relationship)}
                  </td>
                  <td style={blocked ? { color: "#6c757d" } : undefined}>
                    {person.title}
                  </td>
                  <td>
                    <RelationshipCell
                      relationship={relationship}
                      busy={decisionBusy}
                      onConfirm={() =>
                        onDecideMatch(
                          person.apolloId,
                          relationship.match?.contactId ?? "",
                          "confirmed"
                        )
                      }
                      onReject={() =>
                        onDecideMatch(
                          person.apolloId,
                          relationship.match?.contactId ?? "",
                          "rejected"
                        )
                      }
                    />
                  </td>
                  <td className="text-nowrap">
                    {/* `bandLabel` falls back to "Other" for an unknown
                        band, which on a provisional row would invent a
                        verdict for every person the model has not seen
                        yet - the one thing these rows must never do. */}
                    {provisional || unranked.has(person.apolloId)
                      ? "-"
                      : bandLabel(person.band)}
                  </td>
                  <td className="text-muted">
                    {unranked.has(person.apolloId)
                      ? "Not ranked - in HubSpot, not in Apollo's results"
                      : provisional
                        ? "Not yet ranked"
                        : person.reasoning}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </ScrollableRows>
    </div>
  </div>
);

export const ExcludedCandidatesTable: React.FC<{
  eliminated: EliminatedPerson[];
  relationshipOf: (apolloId: string) => Relationship;
  renderSelectCell: (apolloId: string | undefined) => React.ReactNode;
}> = ({ eliminated, relationshipOf, renderSelectCell }) => {
  const [showEliminated, setShowEliminated] = useState(false);
  if (eliminated.length === 0) return null;
  // The reason column appears only when there is something in it, rather than
  // rendering a table of dashes: the model gives a terse reason per person, but a run
  // from before that shipped carries none.
  const anyExclusionReasons = eliminated.some((p) => !!p.reasoning);
  // The BE caps the shortlist and puts the ranked runners-up at the head of this list,
  // each keeping its band; the people it genuinely excluded follow, bandless. Showing
  // the band is what lets the operator take the 4th-best contact when the top three are
  // wrong - without it a runner-up property director reads like a receptionist. Absent
  // on rows discovered before the cap shipped, so the column is conditional too.
  const anyRunnersUp = eliminated.some((p) => !!p.band);
  return (
    <div className="mt-1">
      <Button
        variant="link"
        className="p-0"
        style={{ fontSize: 12, verticalAlign: "baseline" }}
        onClick={() => setShowEliminated((v) => !v)}
      >
        {showEliminated ? "Hide" : "Show"} {eliminated.length}{" "}
        {anyRunnersUp
          ? "more people found, best first"
          : "excluded by the AI ranking"}
      </Button>
      {showEliminated && (
        <ScrollableRows rowCount={eliminated.length}>
          <Table
            size="sm"
            className="mt-1 mb-1 align-middle"
            style={{ fontSize: 12 }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
              }}
            >
              <tr className="text-muted">
                <th style={{ width: 26 }} />
                <th>Name</th>
                <th>Position</th>
                {anyRunnersUp && <th>Priority</th>}
                {anyExclusionReasons && (
                  <th>{anyRunnersUp ? "Why" : "Why excluded"}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {eliminated.map((p, i) => (
                <tr key={p.apolloId ?? `${p.firstName}-${p.title}-${i}`}>
                  {/* Same opted-out guard as the shortlist table above. It
                  matters more here since the shortlist cap: a ranked
                  runner-up now lands in THIS table, and the checkbox is
                  the only gate - there is no server-side opt-out check,
                  so an unguarded tick spends a credit revealing the email
                  of someone who asked not to be contacted. */}
                  {p.apolloId &&
                  relationshipOf(p.apolloId).kind === "do_not_contact" ? (
                    <td>
                      <Form.Check
                        type="checkbox"
                        disabled
                        checked={false}
                        readOnly
                        title="Opted out in HubSpot - cannot be contacted"
                      />
                    </td>
                  ) : (
                    renderSelectCell(p.apolloId)
                  )}
                  <td className="text-nowrap">
                    {candidateDisplayName(
                      p,
                      p.apolloId ? relationshipOf(p.apolloId) : null
                    )}
                  </td>
                  <td>{p.title}</td>
                  {anyRunnersUp && (
                    <td className="text-nowrap">
                      {/* Bandless means the ranking excluded them, not that
                      the band is unknown - so a dash, never "Other",
                      which `bandLabel` would invent here. */}
                      {p.band ? bandLabel(p.band) : "-"}
                    </td>
                  )}
                  {anyExclusionReasons && (
                    <td className="text-muted">{p.reasoning || "-"}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollableRows>
      )}
    </div>
  );
};
